import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { Panel, PanelHead } from '../../components/primitives/Panel';
import { RevisionEntry } from './revisionModel';
import { revisionMessages } from './revisionMessages';
import { RevisionCompareSide } from './RevisionCompareSide';
import { RevisionDiffTable } from './RevisionDiffTable';
import { RevisionComparePicks } from './RevisionComparePicks';
import { useComparePlayback } from './useComparePlayback';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Revisions.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface RevisionCompareProps {
  entries: RevisionEntry[];
  /** The two revision keys to show first (the newest two with a file). */
  initialKeys: string[];
  currentUserId: number | null;
  onClose: () => void;
}

/**
 * Compare two revisions (design d2, web only): the two files side by side, one play button for two
 * videos, the facts with the rows that differ marked, and the list of revisions to pick from. A
 * failed revision and a revision with no file cannot be picked. A new pick replaces the older pick.
 */
const RevisionCompareComponent: React.FC<RevisionCompareProps> = ({ entries, initialKeys, currentUserId, onClose }) => {
  const intl = useIntl();
  const [keys, setKeys] = useState<string[]>(initialKeys.slice(0, 2));
  const toggle = useCallback((key: string) => {
    setKeys((current) => {
      if (current.includes(key)) {
        return current.filter((one) => one !== key);
      }
      return [...current, key].slice(-2);
    });
  }, []);
  const picked = useMemo(
    () =>
      keys
        .map((key) => entries.find((entry) => entry.key === key))
        .filter((entry): entry is RevisionEntry => entry !== undefined)
        .sort((a, b) => b.number - a.number),
    [entries, keys]
  );
  const [left, right] = picked;
  const hasVideo = useMemo(() => picked.some((entry) => entry.viewerItem?.mediaKind === 'video'), [picked]);
  const playback = useComparePlayback(hasVideo);

  return (
    <div className={styles.layer} data-testid="revision-compare">
      <Panel
        className={styles.compareBox}
        role="dialog"
        aria-modal="true"
        aria-label={intl.formatMessage(revisionMessages.compareTitle)}
      >
        <div className={styles.compareSides}>
          {[left, right].map((entry, index) => (
            <RevisionCompareSide
              // eslint-disable-next-line react/no-array-index-key -- the place of a side is its identity
              key={index}
              entry={entry}
              videoRef={index === 0 ? playback.leftRef : playback.rightRef}
            />
          ))}
        </div>
        <div className={styles.compareSidePanel}>
          <PanelHead
            title={intl.formatMessage(revisionMessages.compareTitle)}
            subtitle={intl.formatMessage(revisionMessages.compareHint)}
            right={
              <button type="button" className={styles.linkButton} onClick={onClose}>
                {intl.formatMessage(revisionMessages.compareClose)}
              </button>
            }
          />
          {playback.hasVideo && (
            <button type="button" className={styles.chip} onClick={playback.toggle}>
              {intl.formatMessage(playback.isPlaying ? revisionMessages.comparePause : revisionMessages.comparePlay)}
            </button>
          )}
          {left !== undefined && right !== undefined && (
            <RevisionDiffTable a={left} b={right} currentUserId={currentUserId} />
          )}
          <RevisionComparePicks entries={entries} keys={keys} currentUserId={currentUserId} onToggle={toggle} />
        </div>
      </Panel>
    </div>
  );
};

export const RevisionCompare = TypedMemo(RevisionCompareComponent);
